import { ProjectManifest } from '../types/artifactory';

export const nodePathResolver = {
  /**
   * Resolves a node ID or path to a node ID
   * @param idOrPath Node ID or path
   * @param manifest Project manifest
   * @returns Resolved node ID or null if not found
   */
  resolveToNodeId: (idOrPath: string, manifest: ProjectManifest | null): string | null => {
    if (!manifest || !idOrPath) return null;
    
    // Direct UUID match
    if (manifest.nodes[idOrPath]) {
      return idOrPath;
    }
    
    // Path matching
    if (idOrPath.includes('/')) {
      // Normalize path (with/without leading slash)
      const normalizedPath = idOrPath.startsWith('/') ? idOrPath : `/${idOrPath}`;
      const altPath = idOrPath.startsWith('/') ? idOrPath.substring(1) : `/${idOrPath}`;
      
      // Find node with matching path - exact match first
      for (const [nodeId, node] of Object.entries(manifest.nodes)) {
        if (node.path === idOrPath || node.path === normalizedPath || node.path === altPath) {
          return nodeId;
        }
      }
      
      // If no exact match, try matching the filename part
      const fileName = idOrPath.split('/').pop() || '';
      if (fileName) {
        for (const [nodeId, node] of Object.entries(manifest.nodes)) {
          const nodeName = node.path.split('/').pop() || '';
          if (nodeName === fileName) {
            return nodeId;
          }
        }
      }
    }
    
    return null;
  },
  
  /**
   * Resolves a node ID or path to a node path
   * @param idOrPath Node ID or path
   * @param manifest Project manifest
   * @returns Resolved node path or null if not found
   */
  resolveToPath: (idOrPath: string, manifest: ProjectManifest | null): string | null => {
    if (!manifest || !idOrPath) return null;
    
    // Direct UUID match
    if (manifest.nodes[idOrPath]) {
      return manifest.nodes[idOrPath].path || null;
    }
    
    // Path matching - if it's already a path, verify it exists
    if (idOrPath.includes('/')) {
      // Get the project root from the manifest
      let projectRoot = null;
      for (const node of Object.values(manifest.nodes)) {
        if (node.path && node.path.startsWith('/')) {
          const parts = node.path.split('/');
          if (parts.length > 1 && parts[1]) {
            projectRoot = parts[1];
            break;
          }
        }
      }
      
      // If we found a project root, try to match with it
      if (projectRoot) {
        // Try with project root
        const withProjectRoot = idOrPath.startsWith('/') 
          ? `/${projectRoot}${idOrPath}` 
          : `/${projectRoot}/${idOrPath}`;
        
        // Find node with matching path
        for (const node of Object.values(manifest.nodes)) {
          if (node.path === withProjectRoot) {
            return node.path;
          }
        }
      }
      
      // If no match with project root, try exact path match
      for (const node of Object.values(manifest.nodes)) {
        if (node.path === idOrPath) {
          return node.path;
        }
      }
      
      // Try with/without leading slash
      const normalizedPath = idOrPath.startsWith('/') ? idOrPath : `/${idOrPath}`;
      const altPath = idOrPath.startsWith('/') ? idOrPath.substring(1) : `/${idOrPath}`;
      
      for (const node of Object.values(manifest.nodes)) {
        if (node.path === normalizedPath || node.path === altPath) {
          return node.path;
        }
      }
    }
    
    return null;
  },
  
  /**
   * Checks if a node ID or path is valid in the manifest
   * @param idOrPath Node ID or path
   * @param manifest Project manifest
   * @returns True if valid, false otherwise
   */
  isValid: (idOrPath: string, manifest: ProjectManifest | null): boolean => {
    if (!manifest || !idOrPath) return false;
    
    // Direct UUID match
    if (manifest.nodes[idOrPath]) {
      return true;
    }
    
    // Path matching
    if (idOrPath.includes('/')) {
      // Get the project root from the manifest
      let projectRoot = null;
      for (const node of Object.values(manifest.nodes)) {
        if (node.path && node.path.startsWith('/')) {
          const parts = node.path.split('/');
          if (parts.length > 1 && parts[1]) {
            projectRoot = parts[1];
            break;
          }
        }
      }
      
      // If we found a project root, try to match with it
      if (projectRoot) {
        // Try with project root
        const withProjectRoot = idOrPath.startsWith('/') 
          ? `/${projectRoot}${idOrPath}` 
          : `/${projectRoot}/${idOrPath}`;
        
        // Find node with matching path
        for (const node of Object.values(manifest.nodes)) {
          if (node.path === withProjectRoot) {
            return true;
          }
        }
      }
      
      // If no match with project root, try exact path match
      for (const node of Object.values(manifest.nodes)) {
        if (node.path === idOrPath) {
          return true;
        }
      }
      
      // Try with/without leading slash
      const normalizedPath = idOrPath.startsWith('/') ? idOrPath : `/${idOrPath}`;
      const altPath = idOrPath.startsWith('/') ? idOrPath.substring(1) : `/${idOrPath}`;
      
      for (const node of Object.values(manifest.nodes)) {
        if (node.path === normalizedPath || node.path === altPath) {
          return true;
        }
      }
    }
    
    return false;
  }
};
